import bs58 from 'bs58';
import { blake2b } from '@noble/hashes/blake2b';
import { keccak_256 } from '@noble/hashes/sha3';
import { concatBytes } from '@noble/hashes/utils';

/** Cere's SS58 network prefix. Spec §3.2. */
export const CERE_SS58_PREFIX = 54;

const SS58PRE = new TextEncoder().encode('SS58PRE');

/**
 * SS58 address for the Cere network.
 *
 * SS58 spec (Substrate): for prefixes 0–63 the format is:
 *   [prefix byte] || [32-byte pubkey] || [2-byte checksum]
 * where checksum = blake2b-512("SS58PRE" || prefix || pubkey)[0..2].
 * The whole thing is base58-encoded. We use prefix 54 (Cere) which fits in
 * the 1-byte encoding (< 64).
 *
 * @param edPubkey 32-byte Ed25519 public key.
 */
export function deriveCereAddress(edPubkey: Uint8Array): string {
  if (edPubkey.length !== 32) {
    throw new Error(`expected 32-byte Ed25519 pubkey, got ${edPubkey.length}`);
  }
  const prefix = new Uint8Array([CERE_SS58_PREFIX]);
  const body = concatBytes(prefix, edPubkey);
  const checksum = blake2b(concatBytes(SS58PRE, body), { dkLen: 64 }).slice(0, 2);
  const payload = concatBytes(body, checksum);
  return bs58.encode(payload);
}

/**
 * Solana address: base58(Ed25519 pubkey). No prefix, no checksum.
 *
 * @param edPubkey 32-byte Ed25519 public key.
 */
export function deriveSolanaAddress(edPubkey: Uint8Array): string {
  if (edPubkey.length !== 32) {
    throw new Error(`expected 32-byte Ed25519 pubkey, got ${edPubkey.length}`);
  }
  return bs58.encode(edPubkey);
}

/**
 * Recover the raw 32-byte Ed25519 public key from a Solana address.
 *
 * The Solana address is `base58(edPubkey)` with no prefix or checksum
 * ({@link deriveSolanaAddress}), so a base58 decode yields the pubkey directly.
 * The public key is not secret — it IS the address — so this introduces no key
 * exposure beyond what the address already reveals; the session keys never
 * leave `SessionVault`. The same Ed25519 key backs the Cere SS58 address.
 *
 * @param solanaAddress base58 Solana address.
 * @returns the 32-byte Ed25519 public key.
 */
export function decodeEd25519Pubkey(solanaAddress: string): Uint8Array {
  const pubkey = bs58.decode(solanaAddress);
  if (pubkey.length !== 32) {
    throw new Error(`expected a 32-byte Ed25519 pubkey from the Solana address, got ${pubkey.length}`);
  }
  return pubkey;
}

/**
 * EVM address: '0x' + last 20 bytes of keccak256(secp256k1 uncompressed pubkey X||Y).
 *
 * Accepts either 64-byte (raw X||Y) or 65-byte (0x04||X||Y) input. The 0x04
 * prefix byte is stripped if present.
 *
 * @param secpPubkey secp256k1 public key.
 */
export function deriveEvmAddress(secpPubkey: Uint8Array): string {
  let xy: Uint8Array;
  if (secpPubkey.length === 65 && secpPubkey[0] === 0x04) {
    xy = secpPubkey.slice(1);
  } else if (secpPubkey.length === 64) {
    xy = secpPubkey;
  } else {
    throw new Error(`expected 64-byte (X||Y) or 65-byte (0x04||X||Y) secp256k1 pubkey, got ${secpPubkey.length}`);
  }
  const hash = keccak_256(xy);
  const last20 = hash.slice(12);
  return '0x' + Buffer.from(last20).toString('hex');
}
